import React, { memo, useEffect, useMemo, useState } from 'react';
import { Stack, Typography, Tabs, Tab } from 'investira.react.components';
import AvaliacoesOutputRespostasAvaliacao from './AvaliacoesOutputRespostasAvaliacao';

const pvAgruparQuestoesPorSessao = pQuestoes => {
  return (Array.isArray(pQuestoes) ? pQuestoes : []).reduce((pAcc, pQuestao, pIndex) => {
    const xSessaoKey = pQuestao?.avaliacao_secao_id ?? `sessao-${pIndex}`;

    if (!pAcc[xSessaoKey]) {
      pAcc[xSessaoKey] = {
        id: xSessaoKey,
        sessao: pQuestao?.avaliacao_secao_sigla || '',
        label: pQuestao?.avaliacao_secao || 'Seção',
        questoes: []
      };
    }

    pAcc[xSessaoKey].questoes.push(pQuestao);
    return pAcc;
  }, {});
};

const AvaliacoesOutputRespostas = memo(props => {
  const { data: externalData } = props;
  const [categoria, setCategoria] = useState(0);

  const xSessoesAgrupadas = useMemo(() => {
    return Object.values(pvAgruparQuestoesPorSessao(externalData));
  }, [externalData]);
  const xQuantidadeJustificadas = useMemo(() => {
    return (Array.isArray(externalData) ? externalData : []).filter(pQuestao => {
      return String(pQuestao?.revisor_justificativa || '').trim().length > 0;
    }).length;
  }, [externalData]);

  useEffect(() => {
    if (categoria < xSessoesAgrupadas.length) {
      return;
    }

    setCategoria(0);
  }, [categoria, xSessoesAgrupadas.length]);

  const xSessaoAtual = xSessoesAgrupadas[categoria] || null;
  const xQuestoesSessaoAtual = xSessaoAtual?.questoes || [];
  const xQuestoesFiltradas = xQuestoesSessaoAtual.filter(xQuestao => {
    return String(xQuestao?.revisor_justificativa || '').trim().length > 0;
  });

  if (xSessoesAgrupadas.length === 0) {
    return (
      <Stack py={2}>
        <Typography color="textSecondary" variant="body2">
          Nenhuma resposta disponível para esta avaliação.
        </Typography>
      </Stack>
    );
  }

  if (xQuantidadeJustificadas === 0) {
    return (
      <Stack py={2}>
        <Typography color="textSecondary" variant="body2">
          Nenhuma resposta com justificativa disponível para esta avaliação.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack direction="row" gap={4}>
      <Stack sx={{ minWidth: '200px', maxWidth: '340px', width: '100%' }}>
        <Tabs
          value={categoria}
          onChange={(_pEvent, pCategoria) => setCategoria(pCategoria)}
          orientation="vertical"
          variant="scrollable"
          sx={{ borderRight: 1, borderColor: 'divider', textAlign: 'left' }}>
          {xSessoesAgrupadas.map((xSessao, xIndex) => {
            return (
              <Tab
                key={xSessao.id || `${xSessao.label}-${xIndex}`}
                label={xSessao.label}
                sx={{ textAlign: 'right', alignItems: 'end' }}
              />
            );
          })}
        </Tabs>
      </Stack>
      <Stack gap={3} flexGrow={1}>
        <Stack gap={1}>
          <Stack mb={1}>
            <Typography color="textPrimary" variant="h6">
              {xSessaoAtual?.label || 'Sessão'}
            </Typography>
          </Stack>

          {xQuestoesFiltradas.length === 0 && (
            <Typography color="textSecondary" variant="body2">
              Nenhuma justificativa disponível nesta sessão.
            </Typography>
          )}

          <Stack gap={4}>
            {xQuestoesFiltradas.map((xQuestao, xIndex) => {
              const xNota = xQuestao.revisor_nota ?? null;
              const xSessaoAlias =
                xQuestao?.avaliacao_secao_sigla || xSessaoAtual?.sessao || `${categoria + 1}`;

              return (
                <AvaliacoesOutputRespostasAvaliacao
                  key={xQuestao.avaliacao_questao_id || `${xSessaoAlias}-${xIndex + 1}`}
                  data={xQuestao}
                  nota={xNota}
                  variant="justificativa"
                  aliasQuestionId={`${xSessaoAlias}.${xIndex + 1}`}
                />
              );
            })}
          </Stack>
        </Stack>
      </Stack>
      <Stack sx={{ minWidth: '200px', maxWidth: '340px', width: '100%' }}></Stack>
    </Stack>
  );
});

export default AvaliacoesOutputRespostas;
